import styled from "@emotion/styled";
import Button, { ButtonProps } from "@mui/material/Button";
import useTranslation from "next-translate/useTranslation";
import { ReactNode } from "react";

import AspectRatio from "~/components/common/AspectRatio";
import RichTextViewer from "~/components/common/RichTextViewer";
import WithLink from "~/components/common/WithLink";
import {
  NFT_IMAGE_ASPECT_RATIO_BY_VARIANT,
  NFT_INSTANCE_BASE_PATH_BY_VARIANT
} from "~/components/nfts/NftCard/constants";
import { ELLIPSIS_STUB } from "~/constants/common";
import { DTORichText } from "~/declarations/graphql/types";
import prepareInstanceLink from "~/utils/url/prepareInstanceLink";

import {
  NftCardAspectRatioVariant,
  NftCardInstanceVariant
} from "./declarations";
import NftCardImage from "./NftCardImage";
import {
  nftCardButtonWidthMixin,
  NftCardTitle,
  nftPageFeedCardDescriptionMarginMixin,
  NftPageFeedCardFooter,
  NftPageFeedCardFooterDivider,
  NftPageFeedCardWrapper
} from "./styled";

export type NftCardProps = {
  title: ReactNode;
  description: DTORichText;
  image: string;
  instanceId: number | string;
  children: ReactNode;
  buttonColor?: ButtonProps["color"];
  instanceVariant?: NftCardInstanceVariant;
  aspectRatioVariant?: NftCardAspectRatioVariant;
  rearImage?: string;
};

const NftCard = ({
  title,
  description,
  image,
  rearImage,
  children,
  instanceId,
  buttonColor = "brandPurple",
  instanceVariant = NftCardInstanceVariant.collection,
  aspectRatioVariant = NftCardAspectRatioVariant.square
}: NftCardProps): JSX.Element => {
  const { t } = useTranslation("common");

  return (
    <WithLink
      href={prepareInstanceLink({
        instanceId,
        instanceBasePath: NFT_INSTANCE_BASE_PATH_BY_VARIANT[instanceVariant]
      })}
    >
      <Wrapper>
        <AspectRatio
          ratio={NFT_IMAGE_ASPECT_RATIO_BY_VARIANT[aspectRatioVariant]}
        >
          <NftCardImage image={image} rearImage={rearImage} />
        </AspectRatio>
        <NftCardTitle>{title}</NftCardTitle>
        <Description richText={description} />
        <NftPageFeedCardFooterDivider />
        <NftPageFeedCardFooter>
          {children}
          <DetailsButton color={buttonColor}>
            {t("common:details")}
          </DetailsButton>
        </NftPageFeedCardFooter>
      </Wrapper>
    </WithLink>
  );
};
export default NftCard;

const Wrapper = styled(NftPageFeedCardWrapper)`
  box-sizing: border-box;
  height: 100%;
`;

const Description = styled(RichTextViewer)`
  ${nftPageFeedCardDescriptionMarginMixin};
  display: block;
`;
Description.defaultProps = {
  typographyVariant: "paragraphTertiary",
  expandable: false,
  ellipsis: ELLIPSIS_STUB,
  ellipsisTypographyColor: "text.primary"
};

const DetailsButton = styled(Button)`
  ${nftCardButtonWidthMixin}
`;
DetailsButton.defaultProps = {
  variant: "contained",
  size: "large"
};
